import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Type,
  Bold,
  Italic,
  Strikethrough,
  Upload,
  X,
  AlertCircle,
  Loader,
  CheckCircle,
  Send,
  Phone,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Wand2,
} from "lucide-react";
import WhatsAppPreview from "../components/WhatsAppNewPreview";
import {
  createTemplate,
  submitTemplateForApproval,
  generateTemplateFromAI,
} from "../apis/template";

const CreateTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Store data directly in component-based format
  const [templateData, setTemplateData] = useState({
    name: "",
    category: "UTILITY",
    language: "en_US",
    components: [
      {
        type: "BODY",
        text: "",
        example: null,
      },
    ],
    wabaId: "",
    phoneId: "",
    // UI-only fields (not sent to API)
    headerFile: null,
    headerFilePreview: null,
    header_media_url: null, // Original media URL from duplicate template
    variables: [], // For managing variables in UI
  });

  const [errors, setErrors] = useState({});
  const [textareaRef, setTextareaRef] = useState(null);
  const [bodyCursorPosition, setBodyCursorPosition] = useState(0);
  const [headerCursorPosition, setHeaderCursorPosition] = useState(0);
  const [headerTextareaRef, setHeaderTextareaRef] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [createdTemplateId, setCreatedTemplateId] = useState(null);

  // AI Template Generation States
  const [aiPrompt, setAiPrompt] = useState("");
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(true);
  const [aiError, setAiError] = useState("");

  // Helper functions to work with components structure
  const getComponent = (type) => {
    return templateData.components.find((c) => c.type === type) || null;
  };

  const updateComponent = (type, updates) => {
    setTemplateData((prev) => {
      const components = [...prev.components];
      const index = components.findIndex((c) => c.type === type);
      
      if (index >= 0) {
        components[index] = { ...components[index], ...updates };
      } else {
        components.push({ type, ...updates });
      }
      
      return { ...prev, components };
    });
  };

  const removeComponent = (type) => {
    setTemplateData((prev) => ({
      ...prev,
      components: prev.components.filter((c) => c.type !== type),
    }));
  };

  // Convenience getters
  const getHeaderFormat = () => {
    const header = getComponent("HEADER");
    return header?.format || "NONE";
  };

  const getHeaderText = () => {
    const header = getComponent("HEADER");
    return header?.text || "";
  };

  const getBody = () => {
    const body = getComponent("BODY");
    return body?.text || "";
  };

  const getFooter = () => {
    const footer = getComponent("FOOTER");
    return footer?.text || "";
  };

  const getButtons = () => {
    const buttons = getComponent("BUTTONS");
    return buttons?.buttons || [];
  };

  // Helper function to get WABA account from localStorage
  const getWabaAccount = () => {
    try {
      const savedWaba = localStorage.getItem("selectedWabaAccount");
      return savedWaba ? JSON.parse(savedWaba) : null;
    } catch (err) {
      console.error("Error parsing saved WABA account:", err);
      return null;
    }
  };

  // Load selected WABA account from localStorage on mount
  useEffect(() => {
    const wabaAccount = getWabaAccount();
    if (wabaAccount) {
      setTemplateData((prev) => ({
        ...prev,
        wabaId: wabaAccount.wabaId,
        phoneId: wabaAccount.phoneId,
      }));
    }
  }, []);

  // Handle duplicate template data from navigation
  useEffect(() => {
    if (location.state?.duplicateTemplate) {
      const duplicateData = location.state.duplicateTemplate;

      // Build components array from duplicate data
      const components = [];
      
      // HEADER component
      if (duplicateData.headerFormat && duplicateData.headerFormat !== "NONE") {
        const headerComponent = {
          type: "HEADER",
          format: duplicateData.headerFormat,
        };
        if (duplicateData.headerText) {
          headerComponent.text = duplicateData.headerText;
        }
        components.push(headerComponent);
      }
      
      // BODY component
      components.push({
        type: "BODY",
        text: duplicateData.body || "",
        example: null,
      });
      
      // FOOTER component
      if (duplicateData.footer) {
        components.push({
          type: "FOOTER",
          text: duplicateData.footer,
        });
      }
      
      // BUTTONS component - preserve IDs and extract URL variables
      if (duplicateData.buttons && duplicateData.buttons.length > 0) {
        const buttonsWithIds = duplicateData.buttons.map((btn, index) => {
          // Ensure unique ID - use larger spacing to avoid conflicts
          const buttonId = btn.id && typeof btn.id === 'number' 
            ? btn.id 
            : Date.now() + (index * 10000); // Large spacing to ensure uniqueness
          
          const buttonObj = {
            id: buttonId,
            type: btn.type,
            text: btn.text || "",
            url: btn.url || undefined,
            phone_number: btn.phone_number || undefined,
            example: btn.example || undefined,
          };
          
          // Extract URL variable if URL contains {{variable}}
          if (btn.type === "URL" && btn.url) {
            const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
            if (urlVariables.length === 1) {
              const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
              if (varMatch) {
                // Extract sample from button.example array or btn.urlVariable
                const sample = btn.urlVariable?.sample || 
                              (Array.isArray(btn.example) && btn.example.length > 0
                                ? btn.example[0]
                                : "");
                buttonObj.urlVariable = {
                  name: varMatch[1],
                  sample: sample,
                };
              }
            } else if (urlVariables.length === 0 && btn.urlVariable) {
              // Preserve existing urlVariable if URL doesn't have variables anymore
              buttonObj.urlVariable = btn.urlVariable;
            }
          }
          
          return buttonObj;
        });
        
        components.push({
          type: "BUTTONS",
          buttons: buttonsWithIds,
        });
      }
      
      // For IMAGE/VIDEO/DOCUMENT headers, set headerFilePreview from header_media_url
      const headerFilePreview = 
        ["IMAGE", "VIDEO", "DOCUMENT"].includes(duplicateData.headerFormat) &&
        duplicateData.header_media_url
          ? duplicateData.header_media_url
          : null;

      // Extract variables with sample values from template_json/example
      const variables = [];
      
      // Parse template_json if it exists
      let templateJson = duplicateData.template_json || {};
      if (typeof templateJson === 'string') {
        try {
          templateJson = JSON.parse(templateJson);
        } catch (e) {
          templateJson = {};
        }
      }
      
      const jsonComponents = templateJson.components || [];
      
      // Extract header variables with samples
      const headerComponent = jsonComponents.find(c => c.type === 'HEADER');
      if (headerComponent && headerComponent.format === 'TEXT' && headerComponent.text) {
        const headerVarMatches = headerComponent.text.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match, index) => {
          const varNum = match.replace(/[{}]/g, '');
          const sample = headerComponent.example?.header_text?.[index] || '';
          variables.push({
            id: Date.now() + index,
            name: varNum,
            sample: sample,
            target: 'header',
          });
        });
      }
      
      // Extract body variables with samples
      const bodyComponent = jsonComponents.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const bodyVarMatches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || [];
        bodyVarMatches.forEach((match, index) => {
          const varNum = match.replace(/[{}]/g, '');
          // Get sample from example.body_text array (first element contains all samples)
          const sample = bodyComponent.example?.body_text?.[0]?.[index] || 
                        bodyComponent.example?.body_text?.[index] || '';
          console.log(sample, "sample");
          variables.push({
            id: Date.now() + 1000 + index,
            name: varNum,
            sample: sample,
            target: 'body',
          });
        });
      }
      
      // Extract button URL variables with samples from template_json
      // Store button-specific samples separately (don't merge with body variables)
      const buttonUrlVariableSamples = {}; // Map: varNum -> sample from button.example
      const buttonsComponent = jsonComponents.find(c => c.type === 'BUTTONS');
      if (buttonsComponent && buttonsComponent.buttons) {
        buttonsComponent.buttons.forEach((btn, btnIndex) => {
          if (btn.type === 'URL' && btn.url) {
            const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
            urlVariables.forEach((match) => {
              const varNum = match.replace(/[{}]/g, '');
              // Get sample from button.example array - this is URL-specific
              const sample = Array.isArray(btn.example) && btn.example.length > 0
                ? btn.example[0]
                : '';
              // Store button-specific sample (don't merge with body variables)
              if (sample) {
                buttonUrlVariableSamples[varNum] = sample;
              }
              // Only add to variables if it doesn't exist (for body variables)
              // But don't overwrite body variable samples with button samples
              const existingVar = variables.find(v => v.name === varNum);
              if (!existingVar) {
                // Add as body variable only if it doesn't exist
                variables.push({
                  id: Date.now() + 2000 + btnIndex,
                  name: varNum,
                  sample: sample,
                  target: 'body',
                });
              }
            });
          }
        });
      }
      
      // If variables already exist in duplicateData, merge with samples from template_json
      const existingVariables = duplicateData.variables || [];
      const mergedVariables = existingVariables.map(existingVar => {
        const jsonVar = variables.find(v => 
          v.name === existingVar.name && v.target === existingVar.target
        );
        // Prioritize sample from template_json, then existing sample
        const finalSample = jsonVar?.sample || existingVar.sample || '';
        return {
          ...existingVar,
          sample: finalSample,
        };
      });
      
      // Add any new variables from template_json that weren't in existingVariables
      variables.forEach(jsonVar => {
        const exists = mergedVariables.some(v => 
          v.name === jsonVar.name && v.target === jsonVar.target
        );
        if (!exists) {
          mergedVariables.push(jsonVar);
        }
      });
      
      // Update button urlVariable samples - use button-specific samples, not body variable samples
      if (duplicateData.buttons && duplicateData.buttons.length > 0) {
        const buttonsComponent = components.find(c => c.type === 'BUTTONS');
        if (buttonsComponent && buttonsComponent.buttons) {
          buttonsComponent.buttons.forEach((btn) => {
            if (btn.type === 'URL' && btn.urlVariable) {
              const varNum = btn.urlVariable.name;
              // Prioritize button-specific sample from template_json
              if (buttonUrlVariableSamples[varNum]) {
                btn.urlVariable.sample = buttonUrlVariableSamples[varNum];
              }
              // Otherwise, keep existing sample from btn.urlVariable or btn.example
              // Don't overwrite with body variable sample!
            }
          });
        }
      }

      setTemplateData((prev) => ({
        ...prev,
        name: duplicateData.name || `copy_${duplicateData.originalName || ''}`,
        category: duplicateData.category || "UTILITY",
        language: duplicateData.language || "en_US",
        components,
        wabaId: duplicateData.wabaId || "",
        phoneId: duplicateData.phoneId || "",
        headerFile: null,
        headerFilePreview,
        header_media_url: duplicateData.header_media_url || null, // Store original media URL
        variables: mergedVariables.length > 0 ? mergedVariables : variables,
      }));
      
      // Update component texts (variables are already set correctly above)
      // Only sync variables if they need renumbering (not sequential 1,2,3...)
      const bodyText = duplicateData.body || "";
      const headerText = duplicateData.headerText || "";
      
      if (bodyText) {
        updateComponent("BODY", { text: bodyText });
        
        // Check if variables need renumbering
        const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
        const bodyVarNumbers = bodyVarMatches
          .map((match) => parseInt(match.replace(/[{}]/g, "")))
          .filter((n) => !isNaN(n) && n > 0)
          .sort((a, b) => a - b);
        
        // Only sync if variables are not sequential (e.g., {{1}}, {{3}} instead of {{1}}, {{2}})
        const needsRenumbering = bodyVarNumbers.length > 0 && 
          bodyVarNumbers.some((num, idx) => num !== idx + 1);
        
        if (needsRenumbering) {
          // Sync after state is set to preserve samples
          setTimeout(() => {
            syncVariablesFromText(bodyText, "body", false);
          }, 100);
        }
      }
      
      if (headerText && duplicateData.headerFormat === "TEXT") {
        updateComponent("HEADER", { text: headerText });
        
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        const headerVarNumbers = headerVarMatches
          .map((match) => parseInt(match.replace(/[{}]/g, "")))
          .filter((n) => !isNaN(n) && n > 0)
          .sort((a, b) => a - b);
        
        const needsRenumbering = headerVarNumbers.length > 0 && 
          headerVarNumbers.some((num, idx) => num !== idx + 1);
        
        if (needsRenumbering) {
          setTimeout(() => {
            syncVariablesFromText(headerText, "header", false);
          }, 100);
        }
      }
      
      // Clear the state to prevent re-applying on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Listen for WABA account changes from Header
  useEffect(() => {
    const handleWabaChange = (event) => {
      const selectedAccount = event.detail;
      setTemplateData((prev) => ({
        ...prev,
        wabaId: selectedAccount.wabaId,
        phoneId: selectedAccount.phoneId,
      }));
    };

    const handleStorageChange = () => {
      const wabaAccount = getWabaAccount();
      if (wabaAccount) {
        setTemplateData((prev) => ({
          ...prev,
          wabaId: wabaAccount.wabaId,
          phoneId: wabaAccount.phoneId,
        }));
      }
    };

    // Listen for custom event (same-tab updates)
    window.addEventListener("wabaAccountChanged", handleWabaChange);
    // Listen for storage events (cross-tab updates)
    window.addEventListener("storage", handleStorageChange);
    // Check on focus in case localStorage was updated
    window.addEventListener("focus", handleStorageChange);

    return () => {
      window.removeEventListener("wabaAccountChanged", handleWabaChange);
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    console.log(templateData, "templateData.variables");
  }, [templateData]);

  const handleInputChange = (field, value) => {
    if (["name", "category", "language", "wabaId", "phoneId"].includes(field)) {
      setTemplateData((prev) => ({ ...prev, [field]: value }));
    } else if (field === "body") {
      updateComponent("BODY", { text: value });
    } else if (field === "footer") {
      if (value.trim()) {
        updateComponent("FOOTER", { text: value });
      } else {
        removeComponent("FOOTER");
      }
    } else if (field === "headerFormat") {
      if (value === "NONE") {
        removeComponent("HEADER");
      } else {
        const existingHeader = getComponent("HEADER");
        updateComponent("HEADER", {
          type: "HEADER",
          format: value,
          text: existingHeader?.text || "",
        });
      }
    } else if (field === "headerText") {
      updateComponent("HEADER", { text: value });
    }

    // Real-time validation for template name
    if (field === "name") {
      if (!value.trim()) {
        setErrors((prev) => ({ ...prev, name: "Template name is required" }));
      } else if (value.toLowerCase() === "test") {
        setErrors((prev) => ({ ...prev, name: "Name 'test' is not allowed" }));
      } else if (!/^[a-z0-9_]+$/.test(value)) {
        setErrors((prev) => ({
          ...prev,
          name: "Only lowercase letters, numbers, and underscores allowed",
        }));
      } else {
        setErrors((prev) => ({ ...prev, name: "" }));
      }
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTemplateData((prev) => ({
          ...prev,
          headerFile: file,
          headerFilePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setTemplateData((prev) => ({
      ...prev,
      headerFile: null,
      headerFilePreview: null,
      // Keep header_media_url if it exists (for duplicate templates)
      // Only clear preview if it was from uploaded file
    }));
  };

  const addVariable = (target = "body") => {
    // Get existing variables for the target
    const existingVariables = templateData.variables.filter(
      (v) => v.target === target
    );

    // Check limits: header = 1, body = 10
    if (target === "header" && existingVariables.length >= 1) {
      setErrors((prev) => ({
        ...prev,
        headerText: "Only 1 variable allowed in header",
      }));
      setTimeout(() => {
        setErrors((prev) => ({ ...prev, headerText: "" }));
      }, 3000);
      return;
    }

    if (target === "body" && existingVariables.length >= 10) {
      setErrors((prev) => ({
        ...prev,
        body: "Maximum 10 variables allowed in body",
      }));
      setTimeout(() => {
        setErrors((prev) => ({ ...prev, body: "" }));
      }, 3000);
      return;
    }

    // Get current text and cursor position
    const cursorPos =
      target === "body" ? bodyCursorPosition : headerCursorPosition;
    const currentText =
      target === "body" ? getBody() : getHeaderText();

    // Extract existing variable numbers from text to find next sequential number
    const existingVarMatches = currentText.match(/\{\{(\d+)\}\}/g) || [];
    const existingNumbers = existingVarMatches
      .map((match) => parseInt(match.replace(/[{}]/g, "")))
      .filter((n) => !isNaN(n) && n > 0)
      .sort((a, b) => a - b);

    // Find next sequential number (should be existingNumbers.length + 1)
    const nextNumber = existingNumbers.length > 0 
      ? Math.max(...existingNumbers) + 1 
      : 1;
    
    const variableText = `{{${nextNumber}}}`;

    // Insert variable at cursor position
    const newText =
      currentText.slice(0, cursorPos) +
      variableText +
      currentText.slice(cursorPos);

    // Use syncVariablesFromText to ensure sequential numbering and update variables
    const updatedText = syncVariablesFromText(newText, target, true);

    // Update cursor position after insertion
    setTimeout(() => {
      const textarea = target === "body" ? textareaRef : headerTextareaRef;
      if (textarea) {
        // Calculate new cursor position (accounting for any renumbering)
        const insertedLength = variableText.length;
        const newPos = cursorPos + insertedLength;
        const finalPos = Math.min(newPos, updatedText.length);
        textarea.setSelectionRange(finalPos, finalPos);
        textarea.focus();
      }
    }, 0);
  };

  /**
   * Sync variables from text - extract variables and update variables array
   * Also renumbers variables sequentially (1, 2, 3...)
   * Returns the updated text with renumbered variables
   */
  const syncVariablesFromText = (text, target, updateText = true) => {
    // Use functional update to get latest state
    setTemplateData((prev) => {
      // Extract all variables from text (e.g., {{1}}, {{2}}, {{3}})
      const variableMatches = text.match(/\{\{(\d+)\}\}/g) || [];
      const variableNumbers = variableMatches
        .map((match) => {
          const num = match.replace(/[{}]/g, "");
          return parseInt(num);
        })
        .filter((num) => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);

      // Remove duplicates
      const uniqueNumbers = [...new Set(variableNumbers)];

      // Check limits
      if (target === "header" && uniqueNumbers.length > 1) {
        // Keep only the first variable
        uniqueNumbers.splice(1);
      }
      if (target === "body" && uniqueNumbers.length > 10) {
        // Keep only first 10 variables
        uniqueNumbers.splice(10);
      }

      // Get existing variables for this target from current state
      const existingVariables = prev.variables.filter((v) => v.target === target);
      const otherTargetVariables = prev.variables.filter((v) => v.target !== target);

      // Create a map of old variable numbers to new sequential numbers
      const renumberMap = {};
      uniqueNumbers.forEach((oldNum, index) => {
        const newNum = index + 1;
        renumberMap[oldNum] = newNum;
      });

      // Update text with renumbered variables
      let updatedText = text;
      Object.keys(renumberMap)
        .sort((a, b) => parseInt(b) - parseInt(a)) // Process from highest to lowest to avoid conflicts
        .forEach((oldNum) => {
          const newNum = renumberMap[oldNum];
          if (oldNum !== newNum.toString()) {
            const regex = new RegExp(`\\{\\{${oldNum}\\}\\}`, "g");
            updatedText = updatedText.replace(regex, `{{${newNum}}}`);
          }
        });

      // Remove variables that exceed limits (if any were removed)
      const finalVariableNumbers = uniqueNumbers.slice(0, target === "header" ? 1 : 10);
      
      // Create new variables array with sequential numbering
      // Build a map of old numbers to existing variables for better matching
      const existingVarMap = new Map();
      existingVariables.forEach((v) => {
        const varNum = parseInt(v.name);
        if (!isNaN(varNum)) {
          // Store by both old number and new number (in case already renumbered)
          if (!existingVarMap.has(varNum)) {
            existingVarMap.set(varNum, v);
          }
        }
      });
      
      const newVariables = finalVariableNumbers.map((oldNum, index) => {
        const newNum = index + 1;
        // Find existing variable - try old number first, then check if any variable matches
        let existingVar = existingVarMap.get(oldNum);
        
        // If not found by old number, try to find by position (index)
        if (!existingVar && index < existingVariables.length) {
          // Match by position if variables are already sequential
          const varByPosition = existingVariables[index];
          if (varByPosition && parseInt(varByPosition.name) === newNum) {
            existingVar = varByPosition;
          }
        }
        
        // Preserve sample value - prioritize existing sample, don't default to empty string unnecessarily
        const sampleValue = existingVar?.sample !== undefined && existingVar.sample !== null && existingVar.sample !== ""
          ? existingVar.sample
          : "";
        
        return {
          id: existingVar?.id || Date.now() + index,
          name: newNum.toString(),
          sample: sampleValue,
          target,
        };
      });

      // Update component text with renumbered variables (if requested)
      if (updateText) {
        if (target === "body") {
          updateComponent("BODY", { text: updatedText });
        } else {
          updateComponent("HEADER", { text: updatedText });
        }
      }

      // Return updated state
      return {
        ...prev,
        variables: [...otherTargetVariables, ...newVariables],
      };
    });

    // Return updated text (need to calculate it again for return value)
    const variableMatches = text.match(/\{\{(\d+)\}\}/g) || [];
    const variableNumbers = variableMatches
      .map((match) => parseInt(match.replace(/[{}]/g, "")))
      .filter((num) => !isNaN(num) && num > 0)
      .sort((a, b) => a - b);
    const uniqueNumbers = [...new Set(variableNumbers)];
    const renumberMap = {};
    uniqueNumbers.forEach((oldNum, index) => {
      const newNum = index + 1;
      renumberMap[oldNum] = newNum;
    });
    let updatedText = text;
    Object.keys(renumberMap)
      .sort((a, b) => parseInt(b) - parseInt(a))
      .forEach((oldNum) => {
        const newNum = renumberMap[oldNum];
        if (oldNum !== newNum.toString()) {
          const regex = new RegExp(`\\{\\{${oldNum}\\}\\}`, "g");
          updatedText = updatedText.replace(regex, `{{${newNum}}}`);
        }
      });
    
    return updatedText;
  };

  const updateVariableSample = (variableId, sampleValue) => {
    setTemplateData((prev) => ({
      ...prev,
      variables: prev.variables.map((v) =>
        v.id === variableId ? { ...v, sample: sampleValue } : v
      ),
    }));
  };

  const insertFormatting = (format, target = "body") => {
    const textarea = target === "body" ? textareaRef : headerTextareaRef;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText =
      target === "body" ? getBody() : getHeaderText();
    const selectedText = currentText.substring(start, end);

    let formattedText = "";
    let symbolLength = 0;

    switch (format) {
      case "bold":
        formattedText = `*${selectedText}*`;
        symbolLength = 1;
        break;
      case "italic":
        formattedText = `_${selectedText}_`;
        symbolLength = 1;
        break;
      case "strikethrough":
        formattedText = `~${selectedText}~`;
        symbolLength = 1;
        break;
      default:
        formattedText = selectedText;
    }

    const newText =
      currentText.substring(0, start) +
      formattedText +
      currentText.substring(end);

    // Update component text
    updateComponent(target === "body" ? "BODY" : "HEADER", { text: newText });

    setTimeout(() => {
      if (selectedText) {
        textarea.setSelectionRange(start + symbolLength, end + symbolLength);
      } else {
        textarea.setSelectionRange(start + symbolLength, start + symbolLength);
      }
      textarea.focus();
    }, 0);
  };

  const handleBodyChange = (e) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Sync variables from text (auto-detect, add missing, remove deleted, renumber)
    // This will update both the component text and variables array
    const updatedText = syncVariablesFromText(newText, "body", true);
    
    // Update cursor position
    setBodyCursorPosition(cursorPos);
    
    if (errors.body) {
      setErrors((prev) => ({ ...prev, body: "" }));
    }
    
    // Restore cursor position after state update (if text was renumbered)
    if (updatedText !== newText && textareaRef) {
      setTimeout(() => {
        if (textareaRef) {
          // Try to maintain cursor position as much as possible
          // If variables were renumbered, cursor might shift slightly
          const finalPos = Math.min(cursorPos, updatedText.length);
          textareaRef.setSelectionRange(finalPos, finalPos);
        }
      }, 0);
    }
  };

  const handleHeaderTextChange = (e) => {
    const newText = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    // Sync variables from text (auto-detect, add missing, remove deleted, renumber)
    // This will update both the component text and variables array
    const updatedText = syncVariablesFromText(newText, "header", true);
    
    // Update cursor position
    setHeaderCursorPosition(cursorPos);
    
    if (errors.headerText) {
      setErrors((prev) => ({ ...prev, headerText: "" }));
    }
    
    // Restore cursor position after state update (if text was renumbered)
    if (updatedText !== newText && headerTextareaRef) {
      setTimeout(() => {
        if (headerTextareaRef) {
          // Try to maintain cursor position as much as possible
          const finalPos = Math.min(cursorPos, updatedText.length);
          headerTextareaRef.setSelectionRange(finalPos, finalPos);
        }
      }, 0);
    }
  };

  const addButton = (type = "QUICK_REPLY") => {
    const buttons = getButtons();
    const totalButtons = buttons.length;
    const quickReplyCount = buttons.filter(
      (b) => b.type === "QUICK_REPLY"
    ).length;
    const urlCount = buttons.filter((b) => b.type === "URL").length;
    const callCount = buttons.filter((b) => b.type === "PHONE_NUMBER").length;

    // Check total limit (3 + 2 + 1 = 6)
    if (totalButtons >= 6) return;

    // Check individual type limits
    if (type === "QUICK_REPLY" && quickReplyCount >= 3) return;
    if (type === "URL" && urlCount >= 2) return;
    if (type === "PHONE_NUMBER" && callCount >= 1) return;

    const newButton = {
      id: Date.now(),
      type,
      text: "",
      url: type === "URL" ? "" : undefined,
      phone_number: type === "PHONE_NUMBER" ? "" : undefined,
      example: type === "URL" ? [] : undefined,
      urlVariable: type === "URL" ? null : undefined, // Track URL variable info
    };

    const buttonsComponent = getComponent("BUTTONS");
    if (buttonsComponent) {
      updateComponent("BUTTONS", {
        buttons: [...buttons, newButton],
      });
    } else {
      updateComponent("BUTTONS", {
        type: "BUTTONS",
        buttons: [newButton],
      });
    }
  };

  const updateButton = (id, field, value) => {
    const buttons = getButtons();
    const updatedButtons = buttons.map((btn) => {
      // Use strict comparison to ensure correct button is updated
      if (String(btn.id) === String(id)) {
        const updatedBtn = { ...btn, [field]: value };
        
        // If URL field changed, check for variables and validate
        if (field === "url" && btn.type === "URL") {
          const urlVariables = (value || "").match(/\{\{(\d+)\}\}/g) || [];
          
          // Only allow one variable in URL
          if (urlVariables.length > 1) {
            setErrors((prev) => ({
              ...prev,
              [`url_${id}`]: "Only one variable allowed in URL",
            }));
            setTimeout(() => {
              setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[`url_${id}`];
                return newErrors;
              });
            }, 3000);
            return btn; // Return unchanged button
          }
          
          // Extract variable info if one exists
          if (urlVariables.length === 1) {
            const varMatch = urlVariables[0].match(/\{\{(\d+)\}\}/);
            if (varMatch) {
              // Preserve existing sample if available, otherwise use empty string
              updatedBtn.urlVariable = {
                name: varMatch[1],
                sample: updatedBtn.urlVariable?.sample || "",
              };
            }
          } else {
            // Remove variable info if no variable in URL
            delete updatedBtn.urlVariable;
          }
        }
        
        return updatedBtn;
      }
      return btn; // Return unchanged button
    });
    updateComponent("BUTTONS", { buttons: updatedButtons });
  };

  const updateUrlVariableSample = (buttonId, sampleValue) => {
    const buttons = getButtons();
    const updatedButtons = buttons.map((btn) => {
      if (btn.id === buttonId && btn.type === "URL" && btn.urlVariable) {
        return {
          ...btn,
          urlVariable: {
            ...btn.urlVariable,
            sample: sampleValue,
          },
        };
      }
      return btn;
    });
    updateComponent("BUTTONS", { buttons: updatedButtons });
  };

  const removeButton = (id) => {
    const buttons = getButtons();
    const filteredButtons = buttons.filter((btn) => btn.id !== id);
    
    // Clean up any errors related to this button
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`url_${id}`];
      return newErrors;
    });
    
    if (filteredButtons.length === 0) {
      removeComponent("BUTTONS");
    } else {
      updateComponent("BUTTONS", { buttons: filteredButtons });
    }
  };

  const removeVariable = (id) => {
    const variable = templateData.variables.find((v) => v.id === id);
    if (variable) {
      const target = variable.target === "body" ? "body" : "header";
      const currentText = target === "body" ? getBody() : getHeaderText();
      
      // Remove the variable from text
      const newText = currentText.replace(
        new RegExp(`\\{\\{${variable.name}\\}\\}`, "g"),
        ""
      );

      // Use syncVariablesFromText to renumber remaining variables sequentially
      syncVariablesFromText(newText, target);
    }
  };

  const validateTemplate = () => {
    const newErrors = {};

    if (!templateData.name.trim()) {
      newErrors.name = "Template name is required";
    } else if (templateData.name.toLowerCase() === "test") {
      newErrors.name = "Name 'test' is not allowed";
    } else if (!/^[a-z0-9_]+$/.test(templateData.name)) {
      newErrors.name =
        "Only lowercase letters, numbers, and underscores allowed";
    }

    const body = getBody();
    if (!body.trim()) {
      newErrors.body = "Message body is required";
    }

    if (body.length > 1024) {
      newErrors.body = "Message body cannot exceed 1024 characters";
    }

    // Validate WABA account
    const wabaAccount = getWabaAccount();
    if (!wabaAccount || !wabaAccount.wabaId || !wabaAccount.phoneId) {
      newErrors.wabaId =
        "Please select a WhatsApp Business Account from the header";
    }

    // Validate header based on format
    const headerFormat = getHeaderFormat();
    const headerText = getHeaderText();
    
    if (headerFormat === "TEXT" && !headerText.trim()) {
      newErrors.headerText = "Header text is required when format is TEXT";
    }

    // Validate header variables (max 1)
    const headerVariables = templateData.variables.filter((v) => v.target === "header");
    if (headerVariables.length > 1) {
      newErrors.headerText = "Only 1 variable allowed in header";
    }

    // Validate body variables (max 10)
    const bodyVariables = templateData.variables.filter((v) => v.target === "body");
    if (bodyVariables.length > 10) {
      newErrors.body = "Maximum 10 variables allowed in body";
    }

    // Only require file upload if header_media_url is not present
    if (
      ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat) &&
      !templateData.headerFile &&
      !templateData.header_media_url
    ) {
      newErrors.headerFile = `Please upload a ${headerFormat.toLowerCase()} or use existing media URL`;
    }

    // Validate URL buttons - check for multiple variables
    const buttons = getButtons();
    buttons.forEach((btn) => {
      if (btn.type === "URL" && btn.url) {
        const urlVariables = (btn.url || "").match(/\{\{(\d+)\}\}/g) || [];
        if (urlVariables.length > 1) {
          newErrors[`url_${btn.id}`] = "Only one variable allowed in URL";
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // AI Template Generation Handler
  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      setAiError("Please enter a prompt describing your template");
      return;
    }

    setIsAiGenerating(true);
    setAiError("");

    try {
      const authToken = localStorage.getItem("userAccessToken");
      const response = await generateTemplateFromAI(authToken, {
        prompt: aiPrompt,
        category: templateData.category,
        language: templateData.language,
      });

      if (response.data.success) {
        const generated = response.data.template;

        // Parse variables from body text
        const bodyVariables = [];
        const bodyMatches =
          (generated.body || "").match(/\{\{(\d+)\}\}/g) || [];
        bodyMatches.forEach((match, index) => {
          const varNum = match.replace(/[{}]/g, "");
          bodyVariables.push({
            id: Date.now() + index,
            name: varNum,
            sample: generated.variableSamples?.[`body_${varNum}`] || "",
            target: "body",
          });
        });

        // Parse variables from header text (if TEXT header)
        const headerVariables = [];
        if (generated.headerFormat === "TEXT" && generated.headerText) {
          const headerMatches =
            generated.headerText.match(/\{\{(\d+)\}\}/g) || [];
          headerMatches.forEach((match, index) => {
            const varNum = match.replace(/[{}]/g, "");
            headerVariables.push({
              id: Date.now() + 1000 + index,
              name: varNum,
              sample: generated.variableSamples?.[`header_${varNum}`] || "",
              target: "header",
            });
          });
        }

        // Parse buttons
        const buttons = (generated.buttons || []).map((btn, index) => ({
          id: Date.now() + 2000 + index,
          type: btn.type,
          text: btn.text,
          url: btn.url || undefined,
          phone_number: btn.phone_number || undefined,
          example: btn.example || undefined,
        }));

        // Build components array from AI generated content
        const components = [];
        
        // HEADER component
        if (generated.headerFormat && generated.headerFormat !== "NONE") {
          const headerComponent = {
            type: "HEADER",
            format: generated.headerFormat,
          };
          if (generated.headerText) {
            headerComponent.text = generated.headerText;
          }
          components.push(headerComponent);
        }
        
        // BODY component
        components.push({
          type: "BODY",
          text: generated.body || "",
        });
        
        // FOOTER component
        if (generated.footer) {
          components.push({
            type: "FOOTER",
            text: generated.footer,
          });
        }
        
        // BUTTONS component
        if (buttons.length > 0) {
          components.push({
            type: "BUTTONS",
            buttons: buttons,
          });
        }

        // Update template data with AI generated content
        setTemplateData((prev) => ({
          ...prev,
          name: generated.name || prev.name,
          category: generated.category || prev.category,
          language: generated.language || prev.language,
          components,
          variables: [...bodyVariables, ...headerVariables],
        }));

        // Clear AI prompt and collapse the section
        setAiPrompt("");
        setAiExpanded(false);
        setSuccessMessage(
          "✨ Template generated successfully! Review and customize as needed."
        );
        setTimeout(() => setSuccessMessage(""), 3000);
      }
    } catch (err) {
      console.error("AI Generation Error:", err);
      setAiError(
        err.response?.data?.error ||
          "Failed to generate template. Please try again."
      );
    } finally {
      setIsAiGenerating(false);
    }
  };


  const handleCreateAndSubmit = async () => {
    if (validateTemplate()) {
      setIsLoading(true);
      setErrors({});
      try {
        const authToken = localStorage.getItem("userAccessToken");

        // Build payload directly from state (already in component format)
        // Clone components and add examples for variables
        const components = templateData.components.map((component) => {
          const componentCopy = { ...component };

          // Add examples for BODY component
          if (component.type === "BODY") {
            const bodyVariables = templateData.variables
              .filter((v) => v.target === "body")
              .map((v) => v.sample);
            if (bodyVariables.length > 0) {
              componentCopy.example = {
                body_text: [bodyVariables],
              };
            }
          }

          // Add examples for HEADER component (TEXT format)
          if (component.type === "HEADER" && component.format === "TEXT") {
            const headerVariables = templateData.variables
              .filter((v) => v.target === "header")
              .map((v) => v.sample)
              .filter((sample) => sample && sample.trim() !== ""); // Filter out empty samples
            if (headerVariables.length > 0) {
              componentCopy.example = {
                header_text: headerVariables,
              };
            }
          }

          // Handle media headers (IMAGE, VIDEO, DOCUMENT)
          if (
            component.type === "HEADER" &&
            ["IMAGE", "VIDEO", "DOCUMENT"].includes(component.format)
          ) {
            // If header_media_url exists, use it directly (no upload needed)
            if (templateData.header_media_url) {
              componentCopy.example = {
                header_handle: [templateData.header_media_url],
              };
            } else {
              // Otherwise, use placeholder (will be replaced after upload)
              componentCopy.example = {
                header_handle: ["MEDIA_ID_PLACEHOLDER"],
              };
            }
          }

          // Clean up button objects (remove id field, keep only API fields)
          if (component.type === "BUTTONS" && component.buttons) {
            componentCopy.buttons = component.buttons.map((btn) => {
              const buttonObj = {
                type: btn.type,
                text: btn.text,
              };

              if (btn.type === "URL") {
                if (btn.url) buttonObj.url = btn.url;
                // Handle URL variable example
                if (btn.urlVariable && btn.urlVariable.sample) {
                  buttonObj.example = [btn.urlVariable.sample];
                } else if (
                  btn.example &&
                  Array.isArray(btn.example) &&
                  btn.example.length > 0
                ) {
                  buttonObj.example = btn.example;
                }
              } else if (btn.type === "PHONE_NUMBER") {
                if (btn.phone_number) buttonObj.phone_number = btn.phone_number;
              }

              return buttonObj;
            });
          }

          return componentCopy;
        });

        // Get WABA from localStorage
        const wabaAccount = getWabaAccount();
        const wabaId = wabaAccount?.wabaId || templateData.wabaId;
        const phoneId = wabaAccount?.phoneId || templateData.phoneId;

        // Build final payload
        const templatePayload = {
          name: templateData.name,
          language: templateData.language,
          category: templateData.category.toUpperCase(),
          components,
          waba_id: wabaId,
          phone_id: phoneId,
        };

        const formData = new FormData();
        formData.append("templateData", JSON.stringify(templatePayload));
        formData.append("wabaId", templatePayload.waba_id);
        formData.append("phoneId", templatePayload.phone_id);

        const headerFormat = getHeaderFormat();
        // Only upload file if header_media_url is not present
        // If header_media_url exists, pass it in the request body instead
        if (
          templateData.headerFile &&
          !templateData.header_media_url &&
          ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat)
        ) {
          formData.append("headerFile", templateData.headerFile);
        }
        
        // Pass header_media_url if it exists (for duplicate templates)
        if (templateData.header_media_url) {
          formData.append("header_media_url", templateData.header_media_url);
        }

        const createResponse = await createTemplate(authToken, formData);
        if (createResponse.data.success) {
          const templateId = createResponse.data.metaTemplateId;
          setCreatedTemplateId(templateId);
          navigate("/templates");
        }
      } catch (err) {
        console.error("Error creating/submitting template:", err);

        let errorMessage = "Failed to create/submit template";

        if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
          if (err.response.data.details) {
            errorMessage += `: ${err.response.data.details}`;
          }
        }

        setErrors({ submit: errorMessage });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmitExistingTemplate = async () => {
    if (!createdTemplateId) return;

    setIsLoading(true);
    setErrors({});
    try {
      const authToken = localStorage.getItem("userAccessToken");

      // Get phoneId from global WABA selection
      const wabaAccount = getWabaAccount();
      const phoneId = wabaAccount?.phoneId || templateData.phoneId;

      const response = await submitTemplateForApproval(
        authToken,
        createdTemplateId,
        phoneId
      );

      if (response.data.success) {
        setSuccessMessage(
          `✅ Template submitted to Meta! Status: ${response.data.status}`
        );

        setTimeout(() => {
          navigate("/templates");
        }, 2000);
      }
    } catch (err) {
      console.error("Error submitting template:", err);
      setErrors({
        submit:
          err.response?.data?.error || "Failed to submit template to Meta",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Create preview data object for WhatsAppPreview component (needs flat structure)
  const previewData = {
    headerFormat: getHeaderFormat(),
    headerText: getHeaderText(),
    headerFilePreview: templateData.headerFilePreview || templateData.header_media_url,
    header_media_url: templateData.header_media_url || templateData.headerFilePreview,
    headerFile: templateData.headerFile,
    body: getBody(),
    footer: getFooter(),
    buttons: getButtons(),
    variables: templateData.variables,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar - Fixed */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-5 py-2.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/templates")}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h1 className="text-base lg:text-lg font-bold text-gray-900">
                  Create New Template
                </h1>
                <p className="text-xs text-gray-600">
                  Design your WhatsApp message template
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCreateAndSubmit}
                disabled={isLoading}
                className="px-3 py-1.5 text-xs bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isLoading ? (
                  <Loader size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
                Create & Submit
              </button>

              {createdTemplateId && !isLoading && (
                <button
                  onClick={handleSubmitExistingTemplate}
                  className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-1.5"
                >
                  <Send size={14} />
                  Submit to Meta
                </button>
              )}
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="text-green-600" size={16} />
              <p className="text-green-700 text-xs font-medium">
                {successMessage}
              </p>
            </div>
          )}

          {/* Error Message */}
          {errors.submit && (
            <div className="mt-2 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="text-red-600 flex-shrink-0" size={16} />
              <div>
                <p className="text-red-700 text-xs font-medium">
                  {errors.submit}
                </p>
                <p className="text-red-600 text-[10px] mt-0.5">
                  Please check your template and try again.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-4 lg:px-5 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Template Builder - Scrollable */}
          <div className="lg:col-span-2 lg:pr-4">
            <div className="space-y-4 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto lg:overflow-x-hidden lg:pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {errors.wabaId && (
                <div className="bg-red-50 rounded-lg border border-red-200 p-3">
                  <p className="text-xs text-red-700 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {errors.wabaId}
                  </p>
                </div>
              )}

              {/* AI Template Generator Card */}
              <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-50 rounded-xl border border-purple-200 overflow-hidden shadow-sm">
                <button
                  onClick={() => setAiExpanded(!aiExpanded)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-purple-50/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-violet-500 to-fuchsia-500 rounded-lg shadow-md">
                      <Sparkles size={18} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                        AI Template Generator
                        <span className="text-[10px] font-medium px-2 py-0.5 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white rounded-full">
                          Beta
                        </span>
                      </h2>
                      <p className="text-[11px] text-gray-600">
                        Describe your template and let AI create it for you
                      </p>
                    </div>
                  </div>
                  {aiExpanded ? (
                    <ChevronUp size={18} className="text-purple-600" />
                  ) : (
                    <ChevronDown size={18} className="text-purple-600" />
                  )}
                </button>

                {aiExpanded && (
                  <div className="px-4 pb-4 space-y-3">
                    <div className="relative">
                      <textarea
                        value={aiPrompt}
                        onChange={(e) => {
                          setAiPrompt(e.target.value);
                          if (aiError) setAiError("");
                        }}
                        placeholder="Example: Create a marketing template for a Diwali sale with 30% discount. Include a greeting, offer details, and a Shop Now button linking to our website..."
                        rows={4}
                        className={`w-full px-3 py-2.5 text-sm border ${
                          aiError ? "border-red-300" : "border-purple-200"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent resize-none bg-white/80 backdrop-blur-sm placeholder:text-gray-400`}
                      />
                      <div className="absolute bottom-2 right-2 text-[10px] text-gray-400">
                        {aiPrompt.length}/500
                      </div>
                    </div>

                    {aiError && (
                      <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                        <AlertCircle
                          size={14}
                          className="text-red-500 flex-shrink-0"
                        />
                        <p className="text-xs text-red-600">{aiError}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-gray-500">
                        💡 Tip: Be specific about buttons, variables, and tone
                      </p>
                      <button
                        onClick={handleAiGenerate}
                        disabled={isAiGenerating || !aiPrompt.trim()}
                        className="px-4 py-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-600 hover:to-fuchsia-600 text-white text-xs font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md hover:shadow-lg"
                      >
                        {isAiGenerating ? (
                          <>
                            <Loader size={14} className="animate-spin" />
                            Generating...
                          </>
                        ) : (
                          <>
                            <Wand2 size={14} />
                            Generate Template
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Basic Info Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <h2 className="text-sm font-bold text-gray-900 mb-2.5">
                  Basic Information
                </h2>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Template Name *
                    </label>
                    <input
                      type="text"
                      value={templateData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value.toLowerCase())
                      }
                      placeholder="e.g., welcome_message"
                      className={`w-full px-3 py-2 text-sm border ${
                        errors.name ? "border-red-300" : "border-gray-300"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent`}
                    />
                    {errors.name && (
                      <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                        <AlertCircle size={10} />
                        {errors.name}
                      </p>
                    )}
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      Use lowercase, numbers, and underscores only (no spaces)
                    </p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Category *
                      </label>
                      <select
                        value={templateData.category}
                        onChange={(e) =>
                          handleInputChange("category", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white"
                      >
                        <option value="MARKETING">Marketing</option>
                        <option value="UTILITY">Utility</option>
                        <option value="AUTHENTICATION">Authentication</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Language *
                      </label>
                      <select
                        value={templateData.language}
                        onChange={(e) =>
                          handleInputChange("language", e.target.value)
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white"
                      >
                        <option value="en_US">English (US)</option>
                        <option value="en_GB">English (UK)</option>
                        <option value="hi_IN">Hindi</option>
                        <option value="es_ES">Spanish</option>
                        <option value="fr_FR">French</option>
                        <option value="pt_BR">Portuguese (BR)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              {/* Header Card - Always Visible */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <h2 className="text-sm font-bold text-gray-900 mb-2.5">
                  Header
                </h2>

                <div className="space-y-3">
                  {/* Template Type Selection */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Template Type *
                    </label>
                    <select
                      value={getHeaderFormat()}
                      onChange={(e) => {
                        const newFormat = e.target.value;
                        handleInputChange("headerFormat", newFormat);
                        // Reset file when changing type (but preserve header_media_url for duplicate templates)
                        if (newFormat === "TEXT" || newFormat === "LOCATION") {
                          setTemplateData((prev) => ({
                            ...prev,
                            headerFile: null,
                            headerFilePreview: null,
                            // Keep header_media_url in case user switches back
                          }));
                        }
                      }}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent bg-white"
                    >
                      <option value="NONE">None</option>
                      <option value="TEXT">Text</option>
                      <option value="IMAGE">Image</option>
                      <option value="VIDEO">Video</option>
                      <option value="DOCUMENT">Document</option>
                    </select>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Select the type of media for your template header
                    </p>
                  </div>

                  {getHeaderFormat() === "TEXT" && (
                    <div>
                      <div className="flex items-center justify-between mb-2.5">
                        <label className="block text-xs font-semibold text-gray-700">
                          Header Text *
                        </label>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-gray-500">
                            ({templateData.variables.filter((v) => v.target === "header").length}/1 variable)
                          </span>
                          <button
                            onClick={() => addVariable("header")}
                            disabled={
                              templateData.variables.filter((v) => v.target === "header").length >= 1
                            }
                            className="flex items-center gap-1.5 px-2 py-1 bg-[#25D366]/10 text-[#25D366] text-xs font-medium rounded-lg hover:bg-[#25D366]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Type size={12} />
                            Add Variable
                          </button>
                        </div>
                      </div>
                      <textarea
                        ref={(el) => setHeaderTextareaRef(el)}
                        value={getHeaderText()}
                        onChange={handleHeaderTextChange}
                        onSelect={(e) =>
                          setHeaderCursorPosition(e.target.selectionStart)
                        }
                        placeholder="Enter header text..."
                        rows={2}
                        maxLength={60}
                        className={`w-full px-3 py-2 text-sm border ${
                          errors.headerText
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent resize-none`}
                      />
                      {errors.headerText && (
                        <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                          <AlertCircle size={10} />
                          {errors.headerText}
                        </p>
                      )}
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {getHeaderText().length}/60 characters
                      </p>

                      {/* Header Variable Samples Section */}
                      {templateData.variables.filter((v) => v.target === "header")
                        .length > 0 && (
                        <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <h3 className="text-xs font-semibold text-gray-700 mb-2.5">
                            Header Variable Sample
                          </h3>
                          <div className="space-y-2">
                            {templateData.variables
                              .filter((v) => v.target === "header")
                              .sort((a, b) => {
                                const numA = parseInt(a.name) || 0;
                                const numB = parseInt(b.name) || 0;
                                return numA - numB;
                              })
                              .map((variable) => (
                                <div
                                  key={variable.id}
                                  className="flex items-center gap-2"
                                >
                                  <span className="text-xs font-medium text-gray-700 min-w-[50px]">
                                    {`{{${variable.name}}}`} =
                                  </span>
                                  <input
                                    type="text"
                                    value={variable.sample}
                                    onChange={(e) =>
                                      updateVariableSample(
                                        variable.id,
                                        e.target.value
                                      )
                                    }
                                    placeholder="Enter sample value"
                                    className="flex-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                                  />
                                  <button
                                    onClick={() => removeVariable(variable.id)}
                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remove variable"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {["IMAGE", "VIDEO", "DOCUMENT"].includes(
                    getHeaderFormat()
                  ) && (
                    <div>
                      {/* Show info if header_media_url exists */}
                      {templateData.header_media_url && !templateData.headerFile && (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={16} />
                            <div className="flex-1">
                              <p className="text-xs font-semibold text-blue-900 mb-1">
                                Media URL Available
                              </p>
                              <p className="text-[10px] text-blue-700 mb-2">
                                Using existing media URL. No upload required. You can upload a new file to replace it.
                              </p>
                              <a
                                href={templateData.header_media_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-blue-600 hover:text-blue-800 underline break-all"
                              >
                                {templateData.header_media_url}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!templateData.headerFilePreview && !templateData.header_media_url ? (
                        <div>
                          <label className="block">
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#25D366] transition-colors cursor-pointer">
                              <Upload
                                className="mx-auto mb-2 text-gray-400"
                                size={32}
                              />
                              <p className="text-xs font-semibold text-gray-700 mb-0.5">
                                Click to upload{" "}
                                {getHeaderFormat().toLowerCase()}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {getHeaderFormat() === "IMAGE" &&
                                  "PNG, JPG up to 5MB"}
                                {getHeaderFormat() === "DOCUMENT" &&
                                  "PDF, DOC up to 100MB"}
                                {getHeaderFormat() === "VIDEO" &&
                                  "MP4, AVI up to 16MB"}
                              </p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept={
                                getHeaderFormat() === "IMAGE"
                                  ? "image/*"
                                  : getHeaderFormat() === "DOCUMENT"
                                  ? ".pdf,.doc,.docx"
                                  : "video/*"
                              }
                              onChange={handleFileUpload}
                            />
                          </label>
                        </div>
                      ) : (
                        <div className="relative">
                          {getHeaderFormat() === "IMAGE" && (
                            <div className="relative rounded-lg overflow-hidden border w-fit border-gray-200 bg-gray-50 flex items-center justify-center">
                              <img
                                src={templateData.headerFilePreview || templateData.header_media_url}
                                alt="Preview"
                                className="max-w-[300px] max-h-48 w-auto h-auto object-contain"
                              />
                              {templateData.headerFile && (
                                <button
                                  onClick={removeFile}
                                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                >
                                  <X size={16} />
                                </button>
                              )}
                              {templateData.header_media_url && !templateData.headerFile && (
                                <div className="absolute top-2 right-2 px-2 py-1 bg-blue-500 text-white text-[10px] rounded">
                                  Existing URL
                                </div>
                              )}
                            </div>
                          )}

                          {getHeaderFormat() === "DOCUMENT" && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-3 bg-red-100 rounded-lg">
                                  <svg
                                    className="w-6 h-6 text-red-600"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </div>
                                <div>
                                  <p className="font-semibold text-gray-900">
                                    {templateData.headerFile?.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {(
                                      templateData.headerFile?.size / 1024
                                    ).toFixed(2)}{" "}
                                    KB
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={removeFile}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <X size={20} />
                              </button>
                            </div>
                          )}

                          {getHeaderFormat() === "VIDEO" && (
                            <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center">
                              <video
                                src={templateData.headerFilePreview || templateData.header_media_url}
                                className="max-w-[300px] max-h-48 w-auto h-auto object-contain"
                                controls
                              />
                              {templateData.headerFile && (
                                <button
                                  onClick={removeFile}
                                  className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                >
                                  <X size={16} />
                                </button>
                              )}
                              {templateData.header_media_url && !templateData.headerFile && (
                                <div className="absolute top-2 right-2 px-2 py-1 bg-blue-500 text-white text-[10px] rounded">
                                  Existing URL
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      {errors.headerFile && (
                        <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                          <AlertCircle size={10} />
                          {errors.headerFile}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Body Card - Always Visible */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-2.5">
                  <h2 className="text-sm font-bold text-gray-900">
                    Message Body *
                  </h2>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      ({templateData.variables.filter((v) => v.target === "body").length}/10 variables)
                    </span>
                    <button
                      onClick={() => addVariable("body")}
                      disabled={
                        templateData.variables.filter((v) => v.target === "body").length >= 10
                      }
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#25D366] hover:bg-[#20BD5A] text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Type size={14} />
                      Add Variable
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center gap-1.5 p-1.5 bg-gray-50 rounded-lg border border-gray-200">
                    <button
                      onClick={() => insertFormatting("bold", "body")}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                      title="Bold"
                    >
                      <Bold size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={() => insertFormatting("italic", "body")}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                      title="Italic"
                    >
                      <Italic size={16} className="text-gray-700" />
                    </button>
                    <button
                      onClick={() => insertFormatting("strikethrough", "body")}
                      className="p-1.5 hover:bg-gray-200 rounded transition-colors"
                      title="Strikethrough"
                    >
                      <Strikethrough size={16} className="text-gray-700" />
                    </button>
                  </div>

                  <div>
                    <textarea
                      ref={(el) => setTextareaRef(el)}
                      value={getBody()}
                      onChange={handleBodyChange}
                      onSelect={(e) =>
                        setBodyCursorPosition(e.target.selectionStart)
                      }
                      placeholder="Enter your message here. Click 'Add Variable' to insert {{1}}, {{2}}, etc..."
                      rows={6}
                      maxLength={1024}
                      className={`w-full px-3 py-2 text-sm border ${
                        errors.body ? "border-red-300" : "border-gray-300"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent resize-none font-mono`}
                    />
                    {errors.body && (
                      <p className="text-[10px] text-red-600 mt-0.5 flex items-center gap-1">
                        <AlertCircle size={10} />
                        {errors.body}
                      </p>
                    )}
                    <div className="flex justify-between items-center mt-1.5">
                      <p className="text-[10px] text-gray-500">
                        *bold* _italic_ ~strikethrough~
                      </p>
                      <p
                        className={`text-[10px] ${
                          getBody().length > 1024
                            ? "text-red-600"
                            : "text-gray-500"
                        }`}
                      >
                        {getBody().length}/1024
                      </p>
                    </div>
                  </div>

                  {/* Variable Samples Section */}
                  {templateData.variables.filter((v) => v.target === "body")
                    .length > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <h3 className="text-xs font-semibold text-gray-700 mb-2.5">
                        Variable Samples
                      </h3>
                      <div className="space-y-2">
                        {templateData.variables
                          .filter((v) => v.target === "body")
                          .sort((a, b) => {
                            const numA = parseInt(a.name) || 0;
                            const numB = parseInt(b.name) || 0;
                            return numA - numB;
                          })
                          .map((variable) => (
                            <div
                              key={variable.id}
                              className="flex items-center gap-2"
                            >
                              <span className="text-xs font-medium text-gray-700 min-w-[50px]">
                                {`{{${variable.name}}}`} =
                              </span>
                              <input
                                type="text"
                                value={variable.sample}
                                onChange={(e) =>
                                  updateVariableSample(
                                    variable.id,
                                    e.target.value
                                  )
                                }
                                placeholder="Enter sample value"
                                className="flex-1 px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                              />
                              <button
                                onClick={() => removeVariable(variable.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Remove variable"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Card */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <h2 className="text-sm font-bold text-gray-900 mb-2.5">
                  Footer (Optional)
                </h2>
                <input
                  type="text"
                  value={getFooter()}
                  onChange={(e) => handleInputChange("footer", e.target.value)}
                  placeholder="e.g., Thank you for choosing us!"
                  maxLength={60}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:border-transparent"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {getFooter().length}/60 characters
                </p>
              </div>

              {/* Buttons Card - Always Visible */}
              <div className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <h2 className="text-sm font-bold text-gray-900">Buttons</h2>
                    <span className="text-xs text-gray-500">
                      ({getButtons().length}/6)
                    </span>
                  </div>
                  <div className="flex gap-1.5">
                    {(() => {
                      const buttons = getButtons();
                      const totalButtons = buttons.length;
                      const quickReplyCount = buttons.filter(
                        (b) => b.type === "QUICK_REPLY"
                      ).length;
                      const urlCount = buttons.filter(
                        (b) => b.type === "URL"
                      ).length;
                      const callCount = buttons.filter(
                        (b) => b.type === "PHONE_NUMBER"
                      ).length;

                      const isQuickReplyDisabled =
                        totalButtons >= 6 || quickReplyCount >= 3;
                      const isUrlDisabled = totalButtons >= 6 || urlCount >= 2;
                      const isCallDisabled =
                        totalButtons >= 6 || callCount >= 1;

                      return (
                        <>
                          <button
                            onClick={() => addButton("QUICK_REPLY")}
                            disabled={isQuickReplyDisabled}
                            className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 font-medium rounded transition-colors"
                            title={
                              isQuickReplyDisabled
                                ? `Quick Reply limit reached (${quickReplyCount}/3)`
                                : `Add Quick Reply (${quickReplyCount}/3)`
                            }
                          >
                            + Quick ({quickReplyCount}/3)
                          </button>
                          <button
                            onClick={() => addButton("URL")}
                            disabled={isUrlDisabled}
                            className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 font-medium rounded transition-colors"
                            title={
                              isUrlDisabled
                                ? `URL limit reached (${urlCount}/2)`
                                : `Add URL (${urlCount}/2)`
                            }
                          >
                            + URL ({urlCount}/2)
                          </button>
                          <button
                            onClick={() => addButton("PHONE_NUMBER")}
                            disabled={isCallDisabled}
                            className="px-2 py-1 text-[10px] bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 text-gray-700 font-medium rounded transition-colors"
                            title={
                              isCallDisabled
                                ? `Call limit reached (${callCount}/1)`
                                : `Add Call (${callCount}/1)`
                            }
                          >
                            + Call ({callCount}/1)
                          </button>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {getButtons().length > 0 ? (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                    {getButtons().map((button, index) => {
                      const getTypeBadgeColor = (type) => {
                        if (type === "QUICK_REPLY")
                          return "bg-blue-100 text-blue-700 border-blue-200";
                        if (type === "URL")
                          return "bg-purple-100 text-purple-700 border-purple-200";
                        if (type === "PHONE_NUMBER")
                          return "bg-green-100 text-green-700 border-green-200";
                        return "bg-gray-100 text-gray-700 border-gray-200";
                      };

                      return (
                        <div
                          key={button.id}
                          className="p-2.5 border border-gray-200 rounded-lg bg-gray-50/50 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${getTypeBadgeColor(
                                  button.type
                                )}`}
                              >
                                {button.type === "QUICK_REPLY"
                                  ? "Quick"
                                  : button.type === "PHONE_NUMBER"
                                  ? "Call"
                                  : "URL"}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                #{index + 1}
                              </span>
                            </div>
                            <button
                              onClick={() => removeButton(button.id)}
                              className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Remove button"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>

                          <div className="space-y-1.5">
                            <input
                              type="text"
                              value={button.text}
                              onChange={(e) =>
                                updateButton(button.id, "text", e.target.value)
                              }
                              placeholder="Button text"
                              maxLength={20}
                              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#25D366] focus:border-transparent"
                            />

                            {button.type === "URL" && (
                              <>
                                <input
                                  type="url"
                                  value={button.url || ""}
                                  onChange={(e) =>
                                    updateButton(
                                      button.id,
                                      "url",
                                      e.target.value
                                    )
                                  }
                                  placeholder="https://example.com/track/{{1}}"
                                  className={`w-full px-2 py-1.5 text-xs border ${
                                    errors[`url_${button.id}`]
                                      ? "border-red-300"
                                      : "border-gray-300"
                                  } rounded focus:outline-none focus:ring-1 focus:ring-[#25D366] focus:border-transparent`}
                                />
                                {errors[`url_${button.id}`] && (
                                  <p className="text-[10px] text-red-600 flex items-center gap-1">
                                    <AlertCircle size={10} />
                                    {errors[`url_${button.id}`]}
                                  </p>
                                )}
                                {/* Show variable sample input if URL contains exactly one variable */}
                                {button.url &&
                                  button.urlVariable &&
                                  button.urlVariable.name && (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-medium text-gray-700 min-w-[60px]">
                                          {`{{${button.urlVariable.name}}}`} =
                                        </span>
                                        <input
                                          type="text"
                                          value={button.urlVariable.sample || ""}
                                          onChange={(e) =>
                                            updateUrlVariableSample(
                                              button.id,
                                              e.target.value
                                            )
                                          }
                                          placeholder="Enter sample value (e.g., ORDER_12345)"
                                          className="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#25D366] focus:border-transparent"
                                        />
                                      </div>
                                      <p className="text-[9px] text-gray-500">
                                        Sample value for URL variable preview
                                      </p>
                                    </div>
                                  )}
                              </>
                            )}

                            {button.type === "PHONE_NUMBER" && (
                              <input
                                type="tel"
                                value={button.phone_number || ""}
                                onChange={(e) =>
                                  updateButton(
                                    button.id,
                                    "phone_number",
                                    e.target.value
                                  )
                                }
                                placeholder="+1 555 123 4567"
                                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#25D366] focus:border-transparent"
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-400 text-xs border-2 border-dashed border-gray-200 rounded-lg">
                    No buttons added yet
                  </div>
                )}

                <p className="text-[10px] text-gray-500 mt-2">
                  Quick Reply (max 3) • URL (max 2, 1 variable max) • Call (max 1) • Total:{" "}
                  {getButtons().length}/6
                </p>
              </div>
            </div>
          </div>

          {/* Right: Fixed Preview - Not Scrollable */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-16 lg:overflow-visible lg:z-10">
              <WhatsAppPreview templateData={previewData} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplate;
